import Reveal from "@/components/base/Reveal";

export interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}

interface FeatureGridProps {
  items: FeatureItem[];
  tone?: "light" | "dark";
  accent?: "primary" | "accent" | "secondary";
  columns?: 2 | 3 | 4;
}

const COLUMN_CLASS: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

const ACCENT_ICON: Record<string, string> = {
  primary: "bg-primary-100/70 text-primary-700",
  accent: "bg-accent-100/70 text-accent-700",
  secondary: "bg-secondary-100/70 text-secondary-700",
};

const DARK_ICON: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-300",
  accent: "bg-accent-500/15 text-accent-300",
  secondary: "bg-secondary-500/15 text-secondary-400",
};

export default function FeatureGrid({
  items,
  tone = "light",
  accent = "primary",
  columns = 3,
}: FeatureGridProps) {
  const isDark = tone === "dark";

  return (
    <div className={`grid grid-cols-1 gap-5 ${COLUMN_CLASS[columns]}`}>
      {items.map((item, index) => (
        <Reveal key={item.title} delay={index * 60} className="h-full">
          <article
            className={`flex h-full flex-col rounded-xl border p-6 transition-colors ${
              isDark
                ? "border-background-50/10 bg-background-50/5"
                : "border-background-200 bg-background-50 hover:border-primary-200 hover:bg-background-100/60"
            }`}
          >
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-lg ${
                isDark ? DARK_ICON[accent] : ACCENT_ICON[accent]
              }`}
            >
              <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
            </span>
            <h3
              className={`mt-4 font-heading text-base font-semibold ${
                isDark ? "text-background-50" : "text-foreground-950"
              }`}
            >
              {item.title}
            </h3>
            <p
              className={`mt-2 text-sm leading-relaxed ${
                isDark ? "text-background-300" : "text-foreground-600"
              }`}
            >
              {item.description}
            </p>
          </article>
        </Reveal>
      ))}
    </div>
  );
}